import { usePage } from "@inertiajs/react"
import { AlertCircle, Sparkles } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"

import { ConversationHistory } from "@/components/agent-chat/conversation-history"
import { MessageComposer } from "@/components/agent-chat/message-composer"
import { MessageList } from "@/components/agent-chat/message-list"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { csrfToken } from "@/lib/utils"
import {
  agentConversationExchangesPath,
  agentConversationPath,
  agentConversationsPath,
  agentExchangePath,
  retryAgentExchangePath,
} from "@/routes"
import type {
  AgentConversationDetail,
  AgentConversationSummary,
  AgentExchange,
} from "@/types"

interface ConversationResponse {
  conversation: AgentConversationDetail
  exchanges: AgentExchange[]
}

interface ExchangeResponse {
  exchange: AgentExchange
  conversation: AgentConversationSummary
}

class AgentRequestError extends Error {
  status: number
  code?: string

  constructor(status: number, code?: string) {
    super(code ?? `Request failed with status ${status}`)
    this.status = status
    this.code = code
  }
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.method && init.method !== "GET"
        ? { "X-CSRF-Token": csrfToken() }
        : {}),
      ...init?.headers,
    },
  })
  const payload = (await response.json().catch(() => ({}))) as {
    code?: string
  }
  if (!response.ok) throw new AgentRequestError(response.status, payload.code)
  return payload as T
}

function requestKey() {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function AgentChatSheet({ children }: { children: ReactNode }) {
  const { auth } = usePage().props
  const [open, setOpen] = useState(false)
  const [conversations, setConversations] = useState<
    AgentConversationSummary[]
  >([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<AgentConversationDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<{
    kind: "busy" | "unavailable" | "error"
    message: string
  } | null>(null)
  const submissionRef = useRef<{
    prompt: string
    conversationId: number | null
    key: string
  } | null>(null)
  const viewGenerationRef = useRef(0)
  const activeReadRef = useRef<AbortController | null>(null)
  const storageKey = `finance-agent:last-conversation:${auth.user.id}`

  const beginViewChange = useCallback(() => {
    viewGenerationRef.current += 1
    activeReadRef.current?.abort()
    activeReadRef.current = null
    return viewGenerationRef.current
  }, [])

  const isCurrentView = useCallback(
    (generation: number) => viewGenerationRef.current === generation,
    [],
  )

  const beginRead = useCallback((generation: number) => {
    if (viewGenerationRef.current !== generation) return null

    activeReadRef.current?.abort()
    const controller = new AbortController()
    activeReadRef.current = controller
    return controller
  }, [])

  const finishRead = useCallback((controller: AbortController) => {
    if (activeReadRef.current === controller) activeReadRef.current = null
  }, [])

  const loadConversation = useCallback(
    async (id: number, generation: number) => {
      const controller = beginRead(generation)
      if (!controller) return []

      setLoading(true)
      try {
        const response = await requestJson<ConversationResponse>(
          agentConversationPath(id),
          { signal: controller.signal },
        )
        if (!isCurrentView(generation)) return []

        setDetail({ ...response.conversation, exchanges: response.exchanges })
        setSelectedId(id)
        localStorage.setItem(storageKey, String(id))
        setNotice(null)
        return response.exchanges
      } catch (error) {
        if (!isCurrentView(generation)) return []

        if (error instanceof AgentRequestError && error.status === 404) {
          localStorage.removeItem(storageKey)
          setSelectedId(null)
          setDetail(null)
        } else {
          setNotice({ kind: "error", message: "Could not load this chat." })
        }
        return []
      } finally {
        finishRead(controller)
        if (isCurrentView(generation)) setLoading(false)
      }
    },
    [beginRead, finishRead, isCurrentView, storageKey],
  )

  const loadHistory = useCallback(
    async (generation: number) => {
      const controller = beginRead(generation)
      if (!controller) return

      setLoading(true)
      try {
        const response = await requestJson<{
          conversations: AgentConversationSummary[]
        }>(agentConversationsPath(), { signal: controller.signal })
        if (!isCurrentView(generation)) return

        setConversations(response.conversations)

        const stored = Number(localStorage.getItem(storageKey))
        const selected = response.conversations.some(
          (conversation) => conversation.id === stored,
        )
          ? stored
          : response.conversations[0]?.id
        if (selected) {
          await loadConversation(selected, generation)
        } else if (isCurrentView(generation)) {
          setSelectedId(null)
          setDetail(null)
        }
      } catch (error) {
        if (!isCurrentView(generation)) return

        setNotice({
          kind:
            error instanceof AgentRequestError && error.status === 503
              ? "unavailable"
              : "error",
          message:
            error instanceof AgentRequestError && error.status === 503
              ? "The finance assistant is not configured right now."
              : "Could not load your conversations.",
        })
      } finally {
        finishRead(controller)
        if (isCurrentView(generation)) setLoading(false)
      }
    },
    [beginRead, finishRead, isCurrentView, loadConversation, storageKey],
  )

  useEffect(() => {
    if (!open || !detail) return
    const pending = detail.exchanges.find(
      (exchange) => exchange.state === "pending",
    )
    if (!pending) return

    let cancelled = false
    let timer: number | undefined
    let controller: AbortController | undefined
    const generation = viewGenerationRef.current

    const poll = async () => {
      controller = beginRead(generation) ?? undefined
      if (!controller) return

      try {
        const response = await requestJson<ExchangeResponse>(
          agentExchangePath(pending.id),
          { signal: controller.signal },
        )
        if (cancelled || !isCurrentView(generation)) return
        if (response.exchange.state !== "pending") {
          await loadConversation(response.exchange.conversation_id, generation)
          return
        }
      } catch {
        // Keep the persisted pending state visible until a later refresh.
      } finally {
        finishRead(controller)
      }
      if (!cancelled && isCurrentView(generation)) {
        timer = window.setTimeout(() => void poll(), 2000)
      }
    }

    timer = window.setTimeout(() => void poll(), 2000)
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
      controller?.abort()
    }
  }, [beginRead, detail, finishRead, isCurrentView, loadConversation, open])

  function startNew() {
    beginViewChange()
    localStorage.removeItem(storageKey)
    setSelectedId(null)
    setDetail(null)
    setNotice(null)
    setLoading(false)
  }

  function selectConversation(id: number) {
    const generation = beginViewChange()
    void loadConversation(id, generation)
  }

  function handleOpenChange(nextOpen: boolean) {
    const generation = beginViewChange()
    setOpen(nextOpen)
    if (nextOpen) {
      void loadHistory(generation)
    } else {
      setLoading(false)
    }
  }

  async function submit(prompt: string) {
    if (
      submitting ||
      detail?.exchanges.some((item) => item.state === "pending")
    )
      return false

    const generation = viewGenerationRef.current
    setSubmitting(true)
    setNotice(null)
    const previousSubmission = submissionRef.current
    const key =
      previousSubmission?.prompt === prompt &&
      previousSubmission.conversationId === selectedId
        ? previousSubmission.key
        : requestKey()
    submissionRef.current = {
      prompt,
      conversationId: selectedId,
      key,
    }
    try {
      const url = selectedId
        ? agentConversationExchangesPath(selectedId)
        : agentConversationsPath()
      const response = await requestJson<ExchangeResponse>(url, {
        method: "POST",
        body: JSON.stringify({
          prompt,
          client_request_key: key,
          ...(!auth.user.time_zone
            ? {
                initial_time_zone:
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              }
            : {}),
        }),
      })
      submissionRef.current = null
      setConversations((current) => {
        const index = current.findIndex(
          (conversation) => conversation.id === response.conversation.id,
        )
        if (index === -1) return [response.conversation, ...current]

        return current.map((conversation) =>
          conversation.id === response.conversation.id
            ? response.conversation
            : conversation,
        )
      })
      if (isCurrentView(generation)) {
        setSelectedId(response.conversation.id)
        localStorage.setItem(storageKey, String(response.conversation.id))
        await loadConversation(response.conversation.id, generation)
      }
      return true
    } catch (error) {
      const busy = error instanceof AgentRequestError && error.status === 429
      const unavailable =
        error instanceof AgentRequestError && error.status === 503
      if (isCurrentView(generation)) {
        setNotice({
          kind: busy ? "busy" : unavailable ? "unavailable" : "error",
          message: busy
            ? "The assistant is busy. Your question was not submitted."
            : unavailable
              ? "The finance assistant is not configured right now."
              : "Your question could not be submitted. Please try again.",
        })
      }
      if (error instanceof AgentRequestError) submissionRef.current = null
      return false
    } finally {
      setSubmitting(false)
    }
  }

  async function retry(exchange: AgentExchange) {
    if (submitting) return
    const generation = viewGenerationRef.current
    setSubmitting(true)
    setNotice(null)
    try {
      const response = await requestJson<ExchangeResponse>(
        retryAgentExchangePath(exchange.id),
        { method: "POST", body: JSON.stringify({}) },
      )
      if (isCurrentView(generation)) {
        await loadConversation(response.exchange.conversation_id, generation)
      }
    } catch {
      if (isCurrentView(generation)) {
        setNotice({
          kind: "error",
          message: "This request could not be retried.",
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function deleteConversation(conversation: AgentConversationSummary) {
    if (!window.confirm(`Delete “${conversation.title}”?`)) return
    const generation = beginViewChange()
    setLoading(false)
    try {
      const response = await requestJson<{
        next_conversation_id: number | null
      }>(agentConversationPath(conversation.id), {
        method: "DELETE",
        body: JSON.stringify({}),
      })
      if (!isCurrentView(generation)) return

      if (response.next_conversation_id) {
        await loadConversation(response.next_conversation_id, generation)
      } else {
        localStorage.removeItem(storageKey)
        setSelectedId(null)
        setDetail(null)
      }
      setConversations((current) =>
        current.filter((item) => item.id !== conversation.id),
      )
    } catch {
      if (isCurrentView(generation)) {
        setNotice({
          kind: "error",
          message: "This conversation was not deleted.",
        })
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="h-dvh w-screen max-w-none gap-0 p-0 sm:w-[min(48rem,calc(100vw-2rem))] sm:max-w-3xl">
        <SheetHeader className="border-b pr-12">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="size-4" />
            Finance assistant
          </SheetTitle>
          <SheetDescription>
            Private, read-only analysis of your recorded finances
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <ConversationHistory
            conversations={conversations}
            selectedId={selectedId}
            onNew={startNew}
            onSelect={selectConversation}
            onDelete={(conversation) => void deleteConversation(conversation)}
          />
          <main className="flex min-h-0 min-w-0 flex-1 flex-col">
            {notice && (
              <Alert
                variant={notice.kind === "error" ? "destructive" : "default"}
                className="m-3 w-auto"
              >
                <AlertCircle />
                <AlertTitle>
                  {notice.kind === "busy"
                    ? "Assistant busy"
                    : notice.kind === "unavailable"
                      ? "Assistant unavailable"
                      : "Something went wrong"}
                </AlertTitle>
                <AlertDescription>{notice.message}</AlertDescription>
              </Alert>
            )}
            <MessageList
              detail={detail}
              loading={loading}
              onSuggestion={(prompt) => void submit(prompt)}
              onRetry={(exchange) => void retry(exchange)}
            />
            <MessageComposer
              disabled={
                submitting ||
                notice?.kind === "unavailable" ||
                Boolean(
                  detail?.exchanges.some(
                    (exchange) => exchange.state === "pending",
                  ),
                )
              }
              onSubmit={submit}
            />
          </main>
        </div>
      </SheetContent>
    </Sheet>
  )
}
